import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Typography,
  Alert, Space, Button, Descriptions, Tabs, Progress,
  List, Timeline, Badge
} from 'antd';
import {
  UserOutlined,
  SyncOutlined,
  TeamOutlined,
  DollarOutlined,
  TrophyOutlined,
  AlertOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext'; // 新增
import { usePermissions } from '../../hooks/usePermissions'; // 新增
import io from 'socket.io-client'; // 新增

const { Title, Text } = Typography;

const Monitor = () => {
  const { user } = useAuth(); // 新增
  const { isObserver } = usePermissions(); // 新增
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [systemStats, setSystemStats] = useState(null);
  const [operationLogs, setOperationLogs] = useState([]);
  const [anomalies, setAnomalies] = useState([]); // 新增
  const [liveBets, setLiveBets] = useState([]); // 新增
  const [socket, setSocket] = useState(null); // WebSocket 實例
  const [socketStatus, setSocketStatus] = useState('disconnected'); // 新增連接狀態
  const isConnecting = useRef(false); // 防止重複連接的標記
  const [currentRoundId, setCurrentRoundId] = useState(null); // 🔧 新增：追蹤當前回合

  useEffect(() => {
    loadDashboard();
    loadSystemStats();
    loadOperationLogs();
    loadAnomalies();
    loadLiveBets();
    
    // 每30秒自動更新
    const interval = setInterval(() => {
      loadDashboard();
      loadSystemStats();
      loadAnomalies();
      loadLiveBets();
    }, 30000);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  // 独立的 WebSocket 初始化 useEffect
  useEffect(() => {
    if (user?.id && !socket && !isConnecting.current) {
      console.log('正在初始化 WebSocket 连接...');
      isConnecting.current = true;
      initWebSocket();
    }
    
    // 清理函数：组件银毁时断开连接
    return () => {
      if (socket) {
        console.log('组件银毁，正在断开 WebSocket...');
        socket.disconnect();
        setSocket(null);
        setSocketStatus('disconnected');
        isConnecting.current = false;
      }
    };
  }, [user?.id]); // 只依赖 user.id

  // 新增 WebSocket 初始化
  const initWebSocket = () => {
    if (!user?.id) {
      console.log('WebSocket 初始化：用戶ID不存在');
      return;
    }
    
    if (socket?.connected) {
      console.log('WebSocket 已連接，跳過初始化');
      return;
    }
    
    // 如果存在舊連接，先斷開
    if (socket) {
      console.log('正在斷開舊的 WebSocket 連接...');
      socket.disconnect();
    }

    console.log('正在建立新的 WebSocket 連接...');
    const newSocket = io(process.env.REACT_APP_WS_URL || 'http://localhost:3000', {
      transports: ['websocket', 'polling'],
      forceNew: true, // 強制建立新連接
      timeout: 5000,
      reconnection: false // 關閉自動重連，由我們手動控制
    });

    newSocket.on('connect', () => {
      console.log('連接到 WebSocket');
      setSocketStatus('connected');
      isConnecting.current = false; // 連接成功後重置標記
      // 加入管理員監控室
      newSocket.emit('join-admin-monitoring', { agentId: user.id });
    });

    // 監聽即時更新
    newSocket.on('realtime-update', (data) => {
      console.log('收到即時更新:', data);
      
      // 防止重複處理：檢查時間戳和內容
      const currentTimestamp = data.timestamp;
      const lastUpdateKey = `${user.id}-realtime-update`;
      const lastUpdate = sessionStorage.getItem(lastUpdateKey);
      
      if (lastUpdate) {
        const lastData = JSON.parse(lastUpdate);
        if (lastData.timestamp === currentTimestamp && 
            lastData.onlineUsers === data.onlineUsers) {
          console.log('忽略重複的即時更新');
          return;
        }
      }
      
      // 儲存最新的更新資料
      sessionStorage.setItem(lastUpdateKey, JSON.stringify({
        timestamp: currentTimestamp,
        onlineUsers: data.onlineUsers
      }));
      
      // 更新在線用戶數
      setDashboardData(prev => prev ? {
        ...prev,
        realtime: {
          ...prev.realtime,
          onlineUsers: data.onlineUsers
        }
      } : null);
      
      // 🔧 修復：檢查回合是否變更
      if (data.currentRound && data.currentRound.roundId !== currentRoundId) {
        console.log('新回合開始:', data.currentRound.roundId);
        setCurrentRoundId(data.currentRound.roundId);
        setLiveBets([]); // 清空舊回合的投注記錄
        // 重新載入新回合的投注
        loadLiveBets();
      }
      
      // 🔧 修復：只在有新投注時才更新（且屬於當前回合）
      if (data.latestBet && data.latestBet.roundId === currentRoundId) {
        setLiveBets(prev => {
          // 檢查是否已經存在這筆投注（防止重複）
          const exists = prev.some(bet => 
            bet.userId === data.latestBet.userId && 
            bet.betAmount === data.latestBet.amount &&
            Math.abs(new Date(bet.createdAt) - new Date(data.latestBet.time)) < 1000 // 1秒內的投注視為重複
          );
          
          if (!exists) {
            const newBet = {
              id: `ws-${Date.now()}`, // WebSocket 數據的臨時 ID
              displayName: data.latestBet.user,
              betType: data.latestBet.type,
              betAmount: data.latestBet.amount,
              createdAt: data.latestBet.time,
              roundId: data.latestBet.roundId,
              userId: data.latestBet.userId || 'unknown'
            };
            return [newBet, ...prev.slice(0, 49)]; // 保持最新 50 筆
          }
          return prev; // 不更新如果已經存在
        });
      }
    });

    // 監聽新投注广播（真正的即時更新）
    newSocket.on('new-bet', (data) => {
      console.log('收到新投注:', data);
      
      // 立即重新載入即時投注數據
      loadLiveBets();
    });

    newSocket.on('disconnect', () => {
      console.log('WebSocket 斷線');
      setSocketStatus('disconnected');
      isConnecting.current = false; // 斷線時也重置標記
    });

    setSocket(newSocket);
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/agent/monitor/dashboard');
      setDashboardData(response);
    } catch (error) {
      console.error('載入監控資料失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemStats = async () => {
    try {
      const response = await api.get('/agent/monitor/system-stats');
      setSystemStats(response);
    } catch (error) {
      console.error('載入系統統計失敗:', error);
    }
  };

  const loadOperationLogs = async () => {
    try {
      const response = await api.get('/agent/monitor/operation-logs?limit=10');
      setOperationLogs(response.logs || []);
    } catch (error) {
      console.error('載入操作日誌失敗:', error);
    }
  };

  // 新增異常檢測載入
  const loadAnomalies = async () => {
    try {
      const response = await api.get('/agent/monitor/anomaly-detection');
      setAnomalies(response.anomalies || []);
    } catch (error) {
      console.error('載入異常檢測失敗:', error);
    }
  };

  // 新增即時投注載入
  const loadLiveBets = async () => {
    try {
      const response = await api.get('/agent/monitor/live-bets?limit=50');
      setLiveBets(response.data || []);
      
      // 🔧 記錄當前回合 ID
      if (response.currentRound) {
        setCurrentRoundId(response.currentRound.roundId);
      }
    } catch (error) {
      console.error('載入即時投注失敗:', error);
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'betting': 'processing',
      'closed': 'warning', 
      'playing': 'success',
      'finished': 'default'
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'betting': '下注中',
      'closed': '已封盤',
      'playing': '遊戲中',
      'finished': '已結束'
    };
    return statusMap[status] || status;
  };

  const anomalyColumns = [
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeMap = {
          'FREQUENT_BETTING': '頻繁投注',
          'ABNORMAL_AMOUNT': '異常金額',
          'PATTERN_DETECTED': '模式偵測',
          'NEW_USER_LARGE_BET': '新用戶大額',
          'SUSPICIOUS_WIN_RATE': '可疑勝率',
          'RAPID_LOSS': '快速虧損'
        };
        return <Tag color="orange">{typeMap[type] || type}</Tag>;
      },
    },
    {
      title: '嚴重程度',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => {
        const colorMap = {
          'high': 'red',
          'medium': 'orange',
          'low': 'yellow'
        };
        return <Tag color={colorMap[severity]}>{severity.toUpperCase()}</Tag>;
      },
    },
    {
      title: '用戶',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (name, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ID: {record.userId}
          </Text>
        </Space>
      ),
    },
    {
      title: '詳情',
      dataIndex: 'details',
      key: 'details',
      render: (details) => {
        if (typeof details === 'object') {
          return (
            <div style={{ maxWidth: 200 }}>
              {Object.entries(details).map(([key, value]) => (
                <div key={key} style={{ fontSize: '12px' }}>
                  <Text type="secondary">{key}:</Text> {String(value)}
                </div>
              ))}
            </div>
          );
        }
        return <Text ellipsis={{ tooltip: true }}>{String(details)}</Text>;
      },
    },
    {
      title: '時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
    },
  ];

  const currentBetsColumns = [
    {
      title: '用戶',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 120,
      render: (name, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.userId}
          </Text>
        </Space>
      ),
    },
    {
      title: '投注類型',
      dataIndex: 'betType',
      key: 'betType',
      width: 100,
      render: (betType) => (
        <Tag color="blue" size="small">
          {betType}
        </Tag>
      ),
    },
    {
      title: '投注金額',
      dataIndex: 'betAmount',
      key: 'betAmount',
      width: 100,
      render: (amount) => (
        <Text strong style={{ color: '#1890ff' }}>
          ${amount?.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.betAmount - b.betAmount,
    },
    {
      title: '回合',
      dataIndex: 'roundId',
      key: 'roundId',
      width: 100,
      render: (roundId) => (
        <Text code style={{ fontSize: '11px' }}>
          {roundId || 'N/A'}
        </Text>
      ),
    },
    {
      title: '時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => {
        const betTime = new Date(date);
        const now = new Date();
        const diffMinutes = Math.floor((now - betTime) / (1000 * 60));
        
        return (
          <Space direction="vertical" size="small">
            <Text style={{ fontSize: '11px' }}>
              {betTime.toLocaleString('zh-TW')}
            </Text>
            <Text type="secondary" style={{ fontSize: '10px' }}>
              {diffMinutes < 1 ? '剛剛' : 
               diffMinutes < 60 ? `${diffMinutes}分鐘前` : 
               `${Math.floor(diffMinutes / 60)}小時前`}
            </Text>
          </Space>
        );
      },
      sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    },
  ];

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center', 
        marginBottom: 24,
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: window.innerWidth <= 768 ? 12 : 0
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16,
          flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'
        }}>
          <Title level={2} style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>系統監控</Title>
          {isObserver() && (
            <Tag icon={<EyeOutlined />} color="orange" style={{ fontSize: window.innerWidth <= 768 ? '11px' : '12px' }}>
              {window.innerWidth <= 768 ? '觀察者' : '觀察者模式 - 僅供查看'}
            </Tag>
          )}
          <Badge
            status={socketStatus === 'connected' ? 'success' : 'error'}
            text={`WebSocket ${socketStatus === 'connected' ? '已連接' : '未連接'}`}
            style={{ fontSize: window.innerWidth <= 768 ? '11px' : '12px' }}
          />
        </div>
        <Button
          type="primary"
          icon={<SyncOutlined spin={loading} />}
          onClick={loadDashboard}
          loading={loading}
          size={window.innerWidth <= 768 ? "small" : "middle"}
          style={{ alignSelf: window.innerWidth <= 768 ? 'flex-start' : 'auto' }}
        >
          {loading ? '更新中' : '手動更新'}
        </Button>
      </div>
      
      {dashboardData && (
        <>
          {/* 系統狀態卡片 */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="在線用戶"
                  value={dashboardData.realtime?.onlineUsers || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="活躍代理"
                  value={dashboardData.realtime?.activeAgents || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="今日投注"
                  value={dashboardData.todayStats?.totalBets || 0}
                  suffix="筆"
                  prefix={<TrophyOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="今日營收"
                  value={dashboardData.todayStats?.profit || 0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ 
                    color: (dashboardData.todayStats?.profit || 0) >= 0 ? '#3f8600' : '#cf1322' 
                  }}
                  formatter={(value) => `$${value?.toLocaleString()}`}
                />
              </Card>
            </Col>
          </Row>
          
          {/* 詳細監控面板 */}
          <Card style={{ marginTop: 16 }}>
            <Tabs 
              defaultActiveKey="current" 
              type="card"
              items={[
                {
                  key: 'current',
                  label: '當前狀態',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="當前期數狀態" size="small">
                          {dashboardData.realtime?.currentRound ? (
                            <div>
                              <Descriptions size="small">
                                <Descriptions.Item label="期數ID">
                                  <Text code>{dashboardData.realtime.currentRound.roundId}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="狀態">
                                  <Tag color={getStatusColor(dashboardData.realtime.currentRound.status)}>
                                    {getStatusText(dashboardData.realtime.currentRound.status)}
                                  </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="開始時間">
                                  {new Date(dashboardData.realtime.currentRound.startTime).toLocaleString('zh-TW')}
                                </Descriptions.Item>
                              </Descriptions>
                              
                              {dashboardData.realtime.currentRound.betEndTime && (
                                <div style={{ marginTop: 16 }}>
                                  <Text type="secondary">下注截止時間: </Text>
                                  <Text>{new Date(dashboardData.realtime.currentRound.betEndTime).toLocaleString('zh-TW')}</Text>
                                </div>
                              )}
                            </div>
                          ) : (
                            <Alert 
                              message="目前沒有進行中的回合" 
                              type="info" 
                              icon={<ClockCircleOutlined />}
                            />
                          )}
                        </Card>
                      </Col>
                      
                      <Col xs={24} lg={12}>
                        <Card title="系統警報" size="small">
                          {dashboardData.alerts?.largeBets?.length > 0 ? (
                            <List
                              size="small"
                              dataSource={dashboardData.alerts.largeBets}
                              renderItem={(bet, index) => (
                                <List.Item key={index}>
                                  <Alert
                                    message={
                                      <Space>
                                        <AlertOutlined />
                                        <Text>{bet.displayName} 大額投注 ${bet.betAmount?.toLocaleString()}</Text>
                                      </Space>
                                    }
                                    type="warning"
                                    showIcon={false}
                                    style={{ width: '100%' }}
                                  />
                                </List.Item>
                              )}
                            />
                          ) : (
                            <Alert 
                              message="目前沒有警報" 
                              type="success" 
                              icon={<CheckCircleOutlined />}
                            />
                          )}
                        </Card>
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'bets',
                  label: '即時投注',
                  children: (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                          <Col span={6}>
                            <Statistic
                              title="總投注筆數"
                              value={liveBets.length}
                              suffix="筆"
                              valueStyle={{ fontSize: '18px' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="總投注金額"
                              value={liveBets.reduce((sum, bet) => sum + (bet.betAmount || 0), 0)}
                              prefix="$"
                              formatter={(value) => value.toLocaleString()}
                              valueStyle={{ fontSize: '18px', color: '#1890ff' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="平均投注"
                              value={liveBets.length > 0 ? Math.round(liveBets.reduce((sum, bet) => sum + (bet.betAmount || 0), 0) / liveBets.length) : 0}
                              prefix="$"
                              formatter={(value) => value.toLocaleString()}
                              valueStyle={{ fontSize: '18px', color: '#52c41a' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="更新時間"
                              value={new Date().toLocaleTimeString('zh-TW')}
                              valueStyle={{ fontSize: '14px', color: '#666' }}
                            />
                          </Col>
                        </Row>
                      </div>
                      
                      <Table
                        rowKey="id"
                        columns={currentBetsColumns}
                        dataSource={liveBets}
                        pagination={{
                          pageSize: 20,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total, range) => 
                            `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
                        }}
                        scroll={{ y: 300 }}
                        size="small"
                        rowClassName={(record, index) => {
                          // 突出顯示最新的投注
                          const isRecent = record.id && record.id.toString().startsWith('ws-');
                          return isRecent ? 'recent-bet-row' : '';
                        }}
                      />
                      
                      <style>{`
                        .recent-bet-row {
                          background: linear-gradient(90deg, rgba(24, 144, 255, 0.1), transparent) !important;
                          animation: highlight 2s ease-out;
                        }
                        
                        @keyframes highlight {
                          0% { background: rgba(24, 144, 255, 0.3); }
                          100% { background: rgba(24, 144, 255, 0.1); }
                        }
                      `}</style>
                    </>
                  )
                },
                {
                  key: 'anomalies',
                  label: '異常檢測',
                  children: (
                    <Table
                      rowKey={(record) => record.id || `anomaly-${record.type}-${record.timestamp}`}
                      columns={anomalyColumns}
                      dataSource={anomalies}
                      pagination={false}
                      scroll={{ y: 400 }}
                      size="small"
                    />
                  )
                },
                {
                  key: 'system',
                  label: '系統狀態',
                  children: (
                    systemStats && (
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <Card title="服務器狀態" size="small">
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <div>
                                <Text>CPU 使用率: </Text>
                                <Progress 
                                  percent={systemStats.cpu || 0} 
                                  size="small" 
                                  status={systemStats.cpu > 80 ? 'exception' : 'normal'}
                                />
                              </div>
                              <div>
                                <Text>記憶體使用率: </Text>
                                <Progress 
                                  percent={systemStats.memory || 0} 
                                  size="small"
                                  status={systemStats.memory > 85 ? 'exception' : 'normal'}
                                />
                              </div>
                              <div>
                                <Text>資料庫連線: </Text>
                                <Badge 
                                  status={systemStats.database ? 'success' : 'error'} 
                                  text={systemStats.database ? '正常' : '異常'}
                                />
                              </div>
                            </Space>
                          </Card>
                        </Col>
                        
                        <Col xs={24} md={12}>
                          <Card title="最近操作" size="small">
                          <Timeline 
                          size="small"
                          items={operationLogs.slice(0, 5).map((log, index) => ({
                          key: index,
                          dot: log.action === 'login' ? <PlayCircleOutlined /> : <ClockCircleOutlined />,
                          children: (
                          <div style={{ fontSize: '12px' }}>
                          <Text strong>{log.operator?.name}</Text> {log.action}
                          <br />
                          <Text type="secondary">
                          {new Date(log.createdAt).toLocaleString('zh-TW')}
                          </Text>
                          </div>
                          )
                          }))}
                          />
                          </Card>
                        </Col>
                      </Row>
                    )
                  )
                }
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default Monitor;
